import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useOfflineStorage } from './hooks/useOfflineStorage';
import { usePushNotifications } from './hooks/usePushNotifications';
import { checkActiveNativeRecording } from './hooks/useAudioRecorder';

// mockContacts removed — ShareModal and GroupMembersModal now use useContacts hook
// which loads real iOS contacts on native and shows manual entry on web

// API service for real backend calls
import { getVisits, getGroups, getGroup, getGroupMessages, sendGroupMessage, updateVisit, addGroupMembers, createGroup, shareVisit, removeGroupMember, revokeGroupInvite, updateInviteName, createMemberRequest, approveMemberRequest as apiApproveMemberRequest, denyMemberRequest as apiDenyMemberRequest, deleteAccount as apiDeleteAccount, refreshSession } from './services/apiService';
import { removeBiometricCredentials, updateBiometricToken } from './services/biometricService';
import { isTokenExpired } from './services/tokenService';
import { parseLocalDate, formatLocalYMD } from './utils/dateUtils';

// Components
import ClariMeLogo from './components/ClariMeLogo';
import ConsentScreen from './components/ConsentScreen';
import RecordingScreen from './components/RecordingScreen';
import ProcessingAndSummaryScreen from './components/ProcessingAndSummaryScreen';
import AppointmentDetailView from './components/AppointmentDetailView';
import GroupChatView from './components/GroupChatView';
import GroupsListView from './components/GroupsListView';
import CreateCareGroupView from './components/CreateCareGroupView';
import SummaryCard from './components/SummaryCard';
import { MicIcon, SettingsIcon, ClipboardIcon, UsersIcon } from './components/icons';

// ── Helpers ──────────────────────────────────────────────────────────
// Date parsing/formatting lives in utils/dateUtils.js — see the UTC-shift
// warning there before touching anything date-related.

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const groupAppointmentsByDate = (apts) => {
  const grouped = {
    today: [],
    thisWeek: [],
    thisMonth: [],
    lastThreeMonths: [],
    lastSixMonths: [],
    older: []
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...apts].sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date));

  const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today); monthAgo.setMonth(monthAgo.getMonth() - 1);
  const threeMonthsAgo = new Date(today); threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const sixMonthsAgo = new Date(today); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  sorted.forEach(apt => {
    const aptDate = parseLocalDate(apt.date);
    aptDate.setHours(0, 0, 0, 0);

    if (aptDate.getTime() === today.getTime()) {
      grouped.today.push(apt);
    } else if (aptDate >= weekAgo) {
      grouped.thisWeek.push(apt);
    } else if (aptDate >= monthAgo) {
      grouped.thisMonth.push(apt);
    } else if (aptDate >= threeMonthsAgo) {
      grouped.lastThreeMonths.push(apt);
    } else if (aptDate >= sixMonthsAgo) {
      grouped.lastSixMonths.push(apt);
    } else {
      grouped.older.push(apt);
    }
  });
  return grouped;
};

// ── Timeline Section (reusable for each date bucket) ─────────────────

const TimelineSection = ({ label, appointments, groups, isToday, onSelectAppointment, onSelectGroup }) => {
  if (appointments.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {isToday && <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" />}
        <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-teal-600' : 'text-slate-400'}`}>
          {label}
        </p>
        <div className={`flex-1 h-px bg-gradient-to-r ${isToday ? 'from-teal-200' : 'from-slate-200'} to-transparent`} />
      </div>
      <div className="space-y-3">
        {appointments.map(apt => (
          <SummaryCard
            key={apt.id}
            appointment={apt}
            group={groups.find(g => g.id === apt.groupId)}
            onClick={() => onSelectAppointment(apt)}
            onGroupClick={(g) => onSelectGroup(g)}
            showDate={!isToday}
          />
        ))}
      </div>
    </div>
  );
};

// ── API → Frontend data mapping ─────────────────────────────────────

/**
 * Transform a visit object from the backend API format into the shape
 * the frontend components expect (SummaryCard, AppointmentDetailView).
 *
 * Backend returns: { id, providerName, specialty, visitDate, visitTime, summary (JSON string), status }
 * Frontend needs:  { id, date, time, provider, specialty, summary (object), groupId }
 * Visit length is intentionally never surfaced with a summary (physician
 * decision, Session 80) — do not map durationSeconds into display data.
 */
const normalizeDate = (rawDate) => {
  if (!rawDate) return formatLocalYMD();
  // Postgres/knex may return a JS Date object
  if (rawDate instanceof Date) return formatLocalYMD(rawDate);
  // ISO timestamp string like "2026-03-03T00:00:00.000Z" — strip the time part
  if (typeof rawDate === 'string' && rawDate.includes('T')) return rawDate.split('T')[0];
  // Already "YYYY-MM-DD"
  return rawDate;
};

const mapVisitToAppointment = (visit) => {
  // summary comes as a JSON string from the DB, or already parsed
  let summary = visit.summary;
  if (typeof summary === 'string') {
    try { summary = JSON.parse(summary); } catch { summary = null; }
  }

  // Provide a safe fallback so SummaryCard doesn't crash on summary.keyPoints[0]
  if (!summary || !summary.keyPoints || summary.keyPoints.length === 0) {
    summary = {
      keyPoints: [visit.status === 'complete' ? 'Visit recorded' : `Visit ${visit.status || 'in progress'}...`],
      medications: [],
      followUp: '',
      questionsToAsk: [],
      ...(summary || {}),
    };
  }

  // Provider: prefer DB field, then try AI summary (some models extract it), then fallback
  const providerName = visit.providerName || visit.provider_name
    || summary?.providerName
    || null;

  return {
    id: visit.id,
    date: normalizeDate(visit.visitDate || visit.visit_date),
    time: visit.visitTime || visit.visit_time || '',
    provider: providerName || 'Healthcare Provider',
    specialty: visit.specialty || 'General',
    location: visit.location || '',
    groupId: null, // Will be populated from visit_shares in the future
    summary,
    status: visit.status,
  };
};

// ── Demo Mode Visit (Apple App Review) ──────────────────────────────
// A realistic oncology visit so Apple reviewers see the full experience.

const DEMO_VISIT = {
  id: 'demo-visit-001',
  date: formatLocalYMD(), // Always "today" so it shows at the top
  time: '10:30 AM',
  provider: 'Dr. Sarah Chen',
  specialty: 'Oncology',
  location: 'Ochsner Medical Center',
  groupId: 'demo-group-001',
  sharedAt: '2026-04-27T13:00:00Z',
  status: 'complete',
  summary: {
    headline: 'Stage IIA breast cancer with a favorable outlook. Lumpectomy scheduled for May 7, followed by chemotherapy, radiation, and long-term hormone therapy.',
    familySummary: 'Dr. Chen confirmed the diagnosis and walked through the full treatment plan. The cancer was caught early, the prognosis is good, and the standard treatment for this type works well. Surgery is the first step, then four cycles of chemotherapy over about three months, then radiation. Long-term hormone therapy starts after that. Genetic testing is being arranged because of the family history on her mother\'s side.',
    keyPoints: [
      'Diagnosis confirmed: hormone receptor positive (ER+/PR+), HER2 negative breast cancer, Stage IIA',
      'Tumor measures 2.8 cm. Imaging shows clean margins and no spread to lymph nodes.',
      'Treatment plan: lumpectomy on May 7, then four cycles of AC chemotherapy, then radiation, then five to ten years of hormone therapy',
      'Genetic counseling referral placed for BRCA testing given family history (mother diagnosed at age 52)',
    ],
    medications: [
      {
        name: 'Lorazepam 0.5mg',
        dosage: 'As needed',
        notes: 'For anxiety before surgery or procedures. Maximum twice per week.',
      },
      {
        name: 'Tamoxifen 20mg',
        dosage: 'Once daily',
        notes: 'To begin about four weeks after surgery. Long-term hormone therapy for five to ten years.',
      },
    ],
    followUp: {
      nextAppointment: 'Pre-operative appointment on May 5 at 9:00 AM with Dr. Chen',
      actionItems: [
        'Schedule bloodwork and chest X-ray within the next week',
        'Meet with the genetic counselor by May 11',
        'No food or drink after midnight on May 6 (the night before surgery)',
      ],
      warningSignals: [
        'New or worsening pain in the breast or underarm area',
        'Fever above 100.4°F or chills',
      ],
    },
    questionsToAsk: [
      'How long after surgery before I can return to normal activities?',
      'What side effects should I expect during chemotherapy and how can I manage them?',
      'Should my daughter (age 30) also get genetic testing?',
    ],
  },
};

const DEMO_VISIT_2 = {
  id: 'demo-visit-002',
  date: '2026-04-20',
  time: '2:15 PM',
  provider: 'Dr. James Patel',
  specialty: 'Internal Medicine',
  location: 'Crescent City Family Medicine',
  groupId: 'demo-group-001',
  sharedAt: '2026-04-20T19:30:00Z',
  status: 'complete',
  summary: {
    headline: 'Annual physical with all results in normal range. Cleared from a primary care standpoint for the upcoming surgery.',
    keyPoints: [
      'All vital signs and routine labs within normal limits',
      'Discussed coordination with the oncology team for the upcoming lumpectomy',
      'Cleared for surgery from a primary care standpoint',
    ],
    medications: [
      { name: 'Vitamin D3 1,000 IU', dosage: 'Once daily', notes: 'Continue as part of routine supplementation.' },
    ],
    followUp: 'Routine annual physical in one year, sooner if new concerns arise.',
  },
};

const DEMO_VISIT_3 = {
  id: 'demo-visit-003',
  date: '2026-04-06',
  time: '11:00 AM',
  provider: 'Dr. Marcus Lee',
  specialty: 'Cardiology',
  location: 'Ochsner Medical Center',
  groupId: null,
  status: 'complete',
  summary: {
    headline: 'Cardiology follow-up shows blood pressure well controlled on the current dose of lisinopril. Cleared from a cardiac standpoint for the upcoming surgery.',
    keyPoints: [
      'Blood pressure stable at 122/78 on the current dose of lisinopril',
      'EKG unchanged from the prior visit, no concerning findings',
      'Cleared for upcoming oncologic surgery from a cardiac standpoint',
    ],
    medications: [
      { name: 'Lisinopril 10mg', dosage: 'Once daily', notes: 'Continue current dose. Monitor for cough or dizziness.' },
    ],
    followUp: 'Repeat visit in six months. Call sooner if blood pressure readings are consistently above 140 over 90.',
  },
};

// A pre-loaded Family Care Group so the App Review path can complete
// the share flow without a real backend visit. Members are fictional.
const DEMO_GROUP = {
  id: 'demo-group-001',
  name: 'Family',
  category: 'Family',
  color: 'teal',
  isOwner: true,
  ownerId: 'demo-user-001',
  members: [
    {
      id: 'demo-user-001',
      name: 'Demo',
      phone: '',
      email: 'demo@clarime.health',
      role: 'Patient',
      avatar: '👤',
      hasApp: true,
      isOwner: true,
      isCurrentUser: true,
    },
    {
      id: 'demo-member-001',
      name: 'Sarah (Daughter)',
      phone: '',
      email: 'sarah.demo@example.com',
      role: 'Family',
      avatar: '👤',
      hasApp: true,
      isOwner: false,
      isCurrentUser: false,
    },
    {
      id: 'demo-member-002',
      name: 'Michael (Husband)',
      phone: '',
      email: 'michael.demo@example.com',
      role: 'Family',
      avatar: '👤',
      hasApp: true,
      isOwner: false,
      isCurrentUser: false,
    },
  ],
  pendingInvites: [],
  pendingInviteCount: 0,
  sharedVisits: [],
  memberRequests: [],
  appointments: [],
  messages: [
    {
      id: 'demo-msg-001',
      senderId: 'demo-member-001',
      senderName: 'Sarah (Daughter)',
      text: 'Thanks Mom. I read through it. The May 5 pre-op time works for me, I can drive you.',
      timestamp: '2026-04-27T15:22:00Z',
    },
    {
      id: 'demo-msg-002',
      senderId: 'demo-member-002',
      senderName: 'Michael (Husband)',
      text: 'Same here. I will take the day off on the 7th and be at the hospital. Love you.',
      timestamp: '2026-04-27T15:45:00Z',
    },
  ],
};

// ── Main App ─────────────────────────────────────────────────────────

export default function ClariMeApp({ auth }) {
  const [view, setView] = useState('home');
  const [tab, setTab] = useState(() => {
    // If user arrived via a group join link, start on groups tab
    const pendingGroup = sessionStorage.getItem('clarime_pending_group');
    if (pendingGroup) {
      sessionStorage.removeItem('clarime_pending_group');
      return 'groups';
    }
    return 'home';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteAccountStep, setDeleteAccountStep] = useState(null); // null | 'confirm' | 'finalConfirm' | 'deleting' | 'error'
  const [deleteAccountError, setDeleteAccountError] = useState(null);
  const settingsRef = useRef(null);
  const { orphanedRecordings, dismissRecording } = useOfflineStorage();

  // Close settings dropdown when clicking outside
  useEffect(() => {
    if (!showSettings) return;
    const handleClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSettings]);
  const [appointments, setAppointments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [recordingData, setRecordingData] = useState(null);

  // Native recording recovery: if iOS killed our JS context mid-record, the
  // VoiceRecorder plugin's AVAudioRecorder is still running in the host app
  // process. Probe it on mount; if it's still recording, route the user
  // back into RecordingScreen with the captured state so they can stop and
  // recover the audio. If a marker is set but the recorder is gone, surface
  // a banner so the user knows the recording was lost.
  const [recoverRecording, setRecoverRecording] = useState(null);
  const [lostRecordingNotice, setLostRecordingNotice] = useState(null);
  useEffect(() => {
    let cancelled = false;
    checkActiveNativeRecording().then(result => {
      if (cancelled || !result) return;
      if (result.lost) {
        setLostRecordingNotice({ startedAt: result.startedAt });
      } else {
        setRecoverRecording(result);
        setView('recording');
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Sliding session: trade the current JWT for a fresh 90-day one on every
  // app open, and mirror it into the Face ID Keychain entry. Result: only a
  // user who hasn't opened the app at all in 90 days ever re-authenticates.
  // If the token is already dead, announce it now (clean sign-in screen)
  // rather than letting the user record a visit and fail at upload.
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    if (isTokenExpired(token)) {
      window.dispatchEvent(new CustomEvent('clarime:session-expired'));
      return;
    }
    refreshSession()
      .then((res) => {
        if (res?.idToken) {
          localStorage.setItem('auth_token', res.idToken);
          updateBiometricToken(res.email, res.firstName || auth?.user?.firstName || '', res.idToken);
        }
      })
      .catch(() => {
        // Non-fatal: a network blip just means we keep the current token.
        // A 401 is handled globally by apiService's session-expired event...
        // except /auth/ endpoints are excluded from that, so the very next
        // data call's 401 triggers it instead.
      });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Unread message tracking: { groupId: timestamp } — last-read time per group
  const [lastReadTimestamps, setLastReadTimestamps] = useState(() => {
    try {
      const stored = localStorage.getItem('clarime_last_read');
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });

  const markGroupRead = useCallback((groupId) => {
    setLastReadTimestamps(prev => {
      const updated = { ...prev, [groupId]: new Date().toISOString() };
      try { localStorage.setItem('clarime_last_read', JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const getUnreadCount = useCallback((group) => {
    const lastRead = lastReadTimestamps[group.id];
    if (!lastRead) return group.messages?.length || 0;
    return (group.messages || []).filter(msg => {
      const msgTime = msg.timestamp || msg.createdAt;
      return msgTime && new Date(msgTime) > new Date(lastRead);
    }).length;
  }, [lastReadTimestamps]);

  const totalUnread = groups.reduce((sum, g) => sum + getUnreadCount(g), 0);

  // ── Push notifications ──────────────────────────────────────────────

  const handleNotificationTap = useCallback((data) => {
    console.log('[ClariMe] Notification tapped:', data);
    if (data.type === 'message' && data.groupId) {
      // Navigate to group chat
      const group = groups.find(g => g.id === data.groupId);
      if (group) {
        setSelectedGroup(group);
        setView('chat');
      } else {
        // Group not loaded yet — switch to groups tab
        setTab('groups');
        setView('home');
      }
    } else if (data.type === 'share' && data.groupId) {
      // Navigate to group chat (shared visit appears inline)
      const group = groups.find(g => g.id === data.groupId);
      if (group) {
        setSelectedGroup(group);
        setView('chat');
      } else {
        setTab('groups');
        setView('home');
      }
    }
  }, [groups]);

  const { init: initPush, unregister: unregisterPush } = usePushNotifications({
    onNotificationTap: handleNotificationTap,
  });

  // Initialize push notifications after data loads
  useEffect(() => {
    if (!isLoading && !auth?.user?.isDemoMode) {
      initPush();
    }
  }, [isLoading, auth?.user?.isDemoMode, initPush]);

  // ── Account deletion ──────────────────────────────────────────────
  // Required by App Store Guideline 5.1.1(v). Wipes server-side data
  // (visits, recordings, Care Groups owned by user, messages, invites)
  // and every local trace (auth, biometrics, IndexedDB, localStorage).

  const handleConfirmDeleteAccount = useCallback(async () => {
    setDeleteAccountStep('deleting');
    setDeleteAccountError(null);

    try {
      // 1. Server-side delete (cascades visits, groups owned, messages, etc.)
      await apiDeleteAccount();

      // 2. Unregister push token (server may already be gone, but the device
      //    side still has the token — best-effort, don't block on errors)
      try { await unregisterPush(); } catch {}

      // 3. Wipe biometric credentials from Keychain + biometric-state flag
      try { await removeBiometricCredentials(); } catch {}

      // 4. Wipe IndexedDB offline-recording cache
      try {
        const req = indexedDB.deleteDatabase('clarime-offline');
        await new Promise((resolve) => {
          req.onsuccess = resolve;
          req.onerror = resolve;
          req.onblocked = resolve;
        });
      } catch {}

      // 5. Wipe non-auth localStorage (signOut handles auth_token + user)
      try {
        localStorage.removeItem('clarime_auth_history');
        localStorage.removeItem('clarime_last_read');
        localStorage.removeItem('clarime_active_recording');
      } catch {}

      // 6. Sign out — clears auth_token + auth user state, sends user back
      //    to the landing/auth flow.
      auth?.signOut();
    } catch (err) {
      console.error('[App] Account deletion failed:', err);
      setDeleteAccountError(err?.message || 'Could not delete account. Please try again.');
      setDeleteAccountStep('error');
    }
  }, [auth, unregisterPush]);

  // ── Helpers ────────────────────────────────────────────────────────

  // Convert backend group members + pending invites to frontend shape
  const mapGroupMembers = (members = [], pendingInvites = [], groupIsOwner = false) => {
    const mapped = members.map(m => ({
      id: m.userId,
      name: m.name || (m.role === 'owner' ? 'Patient' : 'Unknown'),
      phone: m.phone || '',
      email: m.email || '',
      role: m.role === 'owner' ? 'Patient' : 'Family',
      avatar: '👤',
      hasApp: true,
      isOwner: m.role === 'owner',
      // isCurrentUser: true only when this member is the owner AND we are viewing our own group
      isCurrentUser: m.role === 'owner' && groupIsOwner,
    }));
    const invited = pendingInvites.map(inv => ({
      id: inv.id,
      name: inv.name || inv.email || 'Invited',
      phone: inv.phone || '',
      email: inv.email || '',
      role: 'Family',
      avatar: '👤',
      hasApp: false,
      isInvited: true,
    }));
    return [...mapped, ...invited];
  };

  // Fetch full details for a list of groups (details + messages in parallel)
  const fetchFullGroups = async (groupList) => {
    const results = await Promise.allSettled(
      groupList.map(async (g) => {
        const [detailRes, msgRes] = await Promise.allSettled([
          getGroup(g.id),
          getGroupMessages(g.id),
        ]);
        const detail = detailRes.status === 'fulfilled' ? detailRes.value.group : null;
        const messages = msgRes.status === 'fulfilled' ? (msgRes.value.messages || []) : [];
        return {
          id: g.id,
          name: detail?.name || g.name,
          category: g.category || 'General',
          color: 'teal',
          isOwner: detail?.isOwner ?? g.isOwner ?? true,
          ownerId: detail?.ownerId || g.owner_id,
          members: detail ? mapGroupMembers(detail.members, detail.pendingInvites, detail?.isOwner ?? g.isOwner ?? true) : [],
          pendingInvites: (detail?.pendingInvites || []).map(inv => ({
            id: inv.id,
            name: inv.name,
            email: inv.email,
            phone: inv.phone,
            status: inv.status,
            emailStatus: inv.emailStatus || null,
            createdAt: inv.createdAt,
          })),
          pendingInviteCount: detail?.pendingInvites?.length || g.pendingInviteCount || 0,
          sharedVisits: detail?.sharedVisits || [],
          memberRequests: detail?.memberRequests || [],
          appointments: [],
          messages,
        };
      })
    );
    return results
      .filter(r => r.status === 'fulfilled')
      .map(r => r.value);
  };

  // ── Load visits & groups from backend on mount ──────────────────────

  const loadDataFromBackend = useCallback(async () => {
    setIsLoading(true);

    // Demo mode: load hardcoded visits and Care Group, skip backend entirely
    if (auth?.user?.isDemoMode) {
      setAppointments([DEMO_VISIT, DEMO_VISIT_2, DEMO_VISIT_3]);
      setGroups([DEMO_GROUP]);
      setIsLoading(false);
      return;
    }

    try {
      // Fetch visits and groups in parallel
      const [visitsResponse, groupsResponse] = await Promise.allSettled([
        getVisits(),
        getGroups(),
      ]);

      // Map backend visits to frontend appointment shape
      if (visitsResponse.status === 'fulfilled' && visitsResponse.value?.visits) {
        const mapped = visitsResponse.value.visits
          .filter(v => v.status === 'complete' || v.status === 'uploaded' || v.status === 'transcribing' || v.status === 'summarizing')
          .map(mapVisitToAppointment);
        setAppointments(mapped);
      }

      // Fetch full group details (members, invites, shared visits, messages)
      if (groupsResponse.status === 'fulfilled' && groupsResponse.value?.groups) {
        const fullGroups = await fetchFullGroups(groupsResponse.value.groups);
        setGroups(fullGroups);
      }
    } catch (err) {
      // Backend might not be running — silently fall back to empty state
      console.warn('Could not load data from backend:', err.message);
    } finally {
      setIsLoading(false);
    }
  }, [auth?.user?.isDemoMode]);

  useEffect(() => {
    loadDataFromBackend();
  }, [loadDataFromBackend]);

  // Refresh just groups (lighter than full reload)
  const refreshGroups = async () => {
    try {
      const groupsResponse = await getGroups();
      if (groupsResponse?.groups) {
        const fullGroups = await fetchFullGroups(groupsResponse.groups);
        setGroups(fullGroups);
      }
    } catch (err) {
      console.warn('Could not refresh groups:', err.message);
    }
  };

  // Refresh a single group's data (members, invites, requests, messages)
  const refreshSingleGroup = async (groupId) => {
    try {
      const [detailRes, msgRes] = await Promise.allSettled([
        getGroup(groupId),
        getGroupMessages(groupId),
      ]);
      const detail = detailRes.status === 'fulfilled' ? detailRes.value.group : null;
      const messages = msgRes.status === 'fulfilled' ? (msgRes.value.messages || []) : [];

      if (detail) {
        setGroups(prev => prev.map(existing => {
          if (existing.id !== groupId) return existing;
          return {
            ...existing,
            members: mapGroupMembers(detail.members, detail.pendingInvites, detail.isOwner ?? existing.isOwner),
            pendingInvites: (detail.pendingInvites || []).map(inv => ({
              id: inv.id, name: inv.name, email: inv.email, phone: inv.phone,
              status: inv.status, emailStatus: inv.emailStatus || null, createdAt: inv.createdAt,
            })),
            pendingInviteCount: detail.pendingInvites?.length || 0,
            memberRequests: detail.memberRequests || [],
            sharedVisits: detail.sharedVisits || existing.sharedVisits,
            messages,
          };
        }));
      }
    } catch (err) {
      console.warn('Could not refresh group:', err.message);
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────

  const handleRecordingComplete = (data) => {
    setRecoverRecording(null);
    setRecordingData(data);
    setView('post-recording');
  };

  const handleRecordingCancel = () => {
    setRecoverRecording(null);
    setView('home');
  };

  const handleSaveRecording = (details) => {

    const now = new Date();
    // Use backend visitId if available, otherwise fall back to timestamp
    const appointmentId = details.visitId || Date.now().toString();

    const shouldCreateNewGroup = !details.groupId && details.shareWith?.length > 0;
    const newGroupId = shouldCreateNewGroup ? `group-${appointmentId}` : details.groupId || null;

    // Use real summary from backend if available, otherwise placeholder
    const summary = details.summary || {
      keyPoints: [
        'Your appointment has been recorded successfully',
        'Your summary will be generated shortly',
        'You will be notified when the summary is ready'
      ],
      medications: [],
      followUp: 'Summary processing in progress...',
      questionsToAsk: []
    };

    const newAppointment = {
      id: appointmentId,
      date: formatLocalYMD(now),
      time: now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      provider: details.provider,
      specialty: details.specialty,
      location: details.location,
      groupId: newGroupId,
      summary,
    };

    setAppointments([newAppointment, ...appointments]);

    setGroups(prevGroups => {
      if (details.groupId) {
        return prevGroups.map(g => {
          if (g.id !== details.groupId) return g;
          return { ...g, appointments: [...(g.appointments || []), appointmentId] };
        });
      }

      if (shouldCreateNewGroup) {
        const groupName = details.newGroupName || `${details.specialty || 'Health'} Care`;
        const newMembers = [
          { id: 'u1', name: 'You', phone: '+1234567890', role: 'Patient', avatar: '👩‍🦳', hasApp: true },
          ...details.shareWith.map((c, idx) => ({
            id: `u_${appointmentId}_${idx}`,
            name: c.name.replace(/\s*\(.*\)\s*/g, '').trim(),
            phone: c.phone,
            role: 'Family',
            avatar: c.avatar || '👤',
            hasApp: false,
          })),
        ];
        const newGroup = {
          id: newGroupId,
          name: groupName,
          category: details.specialty || 'General',
          color: 'teal',
          ownerId: 'u1',
          members: newMembers,
          pendingRequests: [],
          appointments: [appointmentId],
          messages: [],
        };
        return [newGroup, ...prevGroups];
      }

      return prevGroups;
    });

    setRecordingData(null);
    setView('home');

    // Persist provider name and specialty to backend, then refresh
    if (details.visitId && (details.provider || details.specialty)) {
      updateVisit(details.visitId, {
        providerName: details.provider || null,
        specialty: details.specialty || null,
      }).catch(err => console.warn('Failed to update visit metadata:', err.message));
    }

    // Refresh from backend so the visit list is authoritative
    // Small delay so the PATCH has time to land before we re-fetch
    setTimeout(() => loadDataFromBackend(), 500);
  };

  const handleSendMessage = async (groupId, text) => {
    // Optimistic local update
    const tempId = `m${Date.now()}`;
    setGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          messages: [...g.messages, {
            id: tempId,
            senderId: 'u1',
            isMe: true,
            text,
            timestamp: new Date().toISOString(),
            source: 'app'
          }]
        };
      }
      return g;
    }));

    // Persist to backend (also triggers email/push to other members)
    try {
      await sendGroupMessage(groupId, text);
    } catch (err) {
      console.warn('[ClariMe] Failed to send message:', err.message);
    }
  };

  const handleAddMembers = async (groupId, contacts) => {
    const group = groups.find(g => g.id === groupId);
    const namesAdded = contacts.map(c => c.name.replace(/\s*\(.*\)\s*/g, '').trim());
    const namesText = namesAdded.length === 1
      ? namesAdded[0]
      : namesAdded.length === 2
        ? `${namesAdded[0]} and ${namesAdded[1]}`
        : `${namesAdded.slice(0, -1).join(', ')}, and ${namesAdded[namesAdded.length - 1]}`;

    // Non-owner: create requests instead of adding directly
    if (group && !group.isOwner) {
      try {
        for (const c of contacts) {
          await createMemberRequest(groupId, {
            name: c.name.replace(/\s*\(.*\)\s*/g, '').trim(),
            email: c.email || null,
            phone: c.phone || null,
          });
        }
        // Refresh to pick up the new request + system message
        await refreshSingleGroup(groupId);
        return { requested: true };
      } catch (err) {
        console.error('Failed to create member request:', err.message);
        throw err;
      }
    }

    // Owner: add directly
    try {
      const members = contacts.map(c => ({
        name: c.name.replace(/\s*\(.*\)\s*/g, '').trim(),
        email: c.email || null,
        phone: c.phone || null,
        role: 'member',
      }));
      await addGroupMembers(groupId, members);

      // Refresh group from backend to get real member data
      const groupData = await getGroup(groupId);
      const g = groupData.group;

      setGroups(prev => prev.map(existing => {
        if (existing.id !== groupId) return existing;
        return {
          ...existing,
          members: mapGroupMembers(g.members, g.pendingInvites, g.isOwner ?? existing.isOwner),
          pendingInvites: g.pendingInvites || [],
          pendingInviteCount: g.pendingInvites?.length || 0,
          memberRequests: g.memberRequests || [],
          messages: [
            ...existing.messages,
            {
              id: `m${Date.now()}`,
              senderId: 'system',
              text: `${namesText} ${contacts.length === 1 ? 'was' : 'were'} added to the group`,
              timestamp: new Date().toISOString(),
              source: 'system',
            },
          ],
        };
      }));
    } catch (err) {
      console.error('Failed to add members:', err.message);
    }
  };

  const handleRemoveMember = async (groupId, memberId) => {
    try {
      await removeGroupMember(groupId, memberId);
      await refreshSingleGroup(groupId);
    } catch (err) {
      console.error('Failed to remove member:', err.message);
      throw err;
    }
  };

  const handleRevokeInvite = async (groupId, inviteId) => {
    try {
      await revokeGroupInvite(groupId, inviteId);
      await refreshSingleGroup(groupId);
    } catch (err) {
      console.error('Failed to revoke invite:', err.message);
      throw err;
    }
  };

  const handleUpdateInviteName = async (groupId, inviteId, name) => {
    try {
      await updateInviteName(groupId, inviteId, name);
      await refreshSingleGroup(groupId);
    } catch (err) {
      console.error('Failed to update invite name:', err.message);
      throw err;
    }
  };

  const handleApproveMemberRequest = async (groupId, requestId) => {
    try {
      await apiApproveMemberRequest(groupId, requestId);
      await refreshSingleGroup(groupId);
    } catch (err) {
      console.error('Failed to approve request:', err.message);
      throw err;
    }
  };

  const handleDenyMemberRequest = async (groupId, requestId) => {
    try {
      await apiDenyMemberRequest(groupId, requestId);
      await refreshSingleGroup(groupId);
    } catch (err) {
      console.error('Failed to deny request:', err.message);
      throw err;
    }
  };

  // ── View routing ──────────────────────────────────────────────────

  if (view === 'consent') {
    return <ConsentScreen onConsent={() => setView('recording')} onCancel={() => setView('home')} />;
  }
  if (view === 'recording') {
    return <RecordingScreen onComplete={handleRecordingComplete} onCancel={handleRecordingCancel} recover={recoverRecording} />;
  }
  if (view === 'post-recording' && recordingData) {
    return (
      <ProcessingAndSummaryScreen
        recordingData={recordingData}
        groups={groups}
        onSave={handleSaveRecording}
        onCancel={() => { setRecordingData(null); setView('home'); }}
      />
    );
  }
  if (view === 'appointment' && selectedAppointment) {
    const group = groups.find(g => g.id === selectedAppointment.groupId);
    return (
      <AppointmentDetailView
        appointment={selectedAppointment}
        group={group}
        groups={groups}
        onBack={() => { setView('home'); setSelectedAppointment(null); }}
        onOpenGroupChat={(g) => { setSelectedGroup(g); setSelectedAppointment(null); setView('chat'); }}
        onUpdateAppointment={(id, updates) => {
          setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
          setSelectedAppointment(prev => prev && prev.id === id ? { ...prev, ...updates } : prev);
        }}
        onShare={async ({ appointmentId, groupIds, contacts: sharedContacts }) => {
          // Demo mode: simulate share locally without hitting backend
          if (auth?.user?.isDemoMode) {
            const gid = groupIds?.[0] || DEMO_GROUP.id;
            setAppointments(prev => prev.map(apt =>
              apt.id === appointmentId ? { ...apt, groupId: gid } : apt
            ));
            setSelectedAppointment(prev =>
              prev && prev.id === appointmentId ? { ...prev, groupId: gid } : prev
            );
            return;
          }

          try {
            let allGroupIds = [...(groupIds || [])];

            // If sharing with new individual contacts, create a new Care Group
            if (sharedContacts?.length > 0) {
              const groupName = `${selectedAppointment.specialty || 'Health'} Care`;
              const result = await createGroup({ name: groupName });
              const newGroupId = result.group.id;

              // Add the contacts as members. Suppress the invitation email:
              // the share email (with the summary link) follows right after,
              // and two emails per share confused real recipients.
              const members = sharedContacts.map(c => ({
                name: c.name.replace(/\s*\(.*\)\s*/g, '').trim(),
                email: c.email || null,
                phone: c.phone || null,
                role: 'member',
              }));
              await addGroupMembers(newGroupId, members, { suppressInviteEmail: true });
              allGroupIds.push(newGroupId);
            }

            // Share visit with each group; surface the share link + email count to the modal
            let shareUrl = null;
            let emailsSent = 0;
            for (const gid of allGroupIds) {
              const result = await shareVisit(appointmentId, gid);
              if (!shareUrl && result?.shareUrl) shareUrl = result.shareUrl;
              emailsSent += result?.emailsSent || 0;
            }

            // Link appointment to first group (for the detail view card)
            if (allGroupIds.length > 0) {
              const gid = allGroupIds[0];
              setAppointments(prev => prev.map(apt =>
                apt.id === appointmentId && !apt.groupId ? { ...apt, groupId: gid } : apt
              ));
              // Also update the currently selected appointment so the detail view reflects the share
              setSelectedAppointment(prev =>
                prev && prev.id === appointmentId ? { ...prev, groupId: gid } : prev
              );
            }

            // Refresh all groups from backend
            await refreshGroups();

            return { shareUrl, emailsSent };
          } catch (err) {
            console.error('Failed to share visit:', err.message);
            // Re-throw so the caller knows it failed
            throw err;
          }
        }}
      />
    );
  }
  if (view === 'chat' && selectedGroup) {
    const currentGroup = groups.find(g => g.id === selectedGroup.id) || selectedGroup;
    return (
      <GroupChatView
        group={currentGroup}
        appointments={appointments}
        onBack={() => { setView('home'); setSelectedGroup(null); }}
        onSendMessage={handleSendMessage}
        onAddMembers={handleAddMembers}
        onRemoveMember={handleRemoveMember}
        onRevokeInvite={handleRevokeInvite}
        onUpdateInviteName={handleUpdateInviteName}
        onApproveMemberRequest={handleApproveMemberRequest}
        onDenyMemberRequest={handleDenyMemberRequest}
        onSelectAppointment={(apt) => { setSelectedAppointment(apt); setView('appointment'); }}
        onMarkRead={markGroupRead}
        onRefreshMessages={async (groupId) => {
          try {
            const res = await getGroupMessages(groupId);
            if (res?.messages) {
              setGroups(prev => prev.map(g =>
                g.id === groupId ? { ...g, messages: res.messages } : g
              ));
            }
          } catch (err) {
            // Silent — polling failure isn't critical
          }
        }}
      />
    );
  }
  if (view === 'create-group') {
    return (
      <CreateCareGroupView
        onSave={async ({ name, members }) => {
          try {
            const result = await createGroup({ name });
            const newGroupId = result.group.id;

            // Add members if any
            if (members.length > 0) {
              const memberData = members.map(m => ({
                name: m.name,
                email: m.email || null,
                phone: m.phone || null,
                role: 'member',
              }));
              await addGroupMembers(newGroupId, memberData);
            }

            // Refresh groups from backend
            await refreshGroups();
            setTab('groups');
            setView('home');
          } catch (err) {
            console.error('Failed to create group:', err.message);
          }
        }}
        onCancel={() => setView('home')}
      />
    );
  }

  // ── Home view ─────────────────────────────────────────────────────

  const groupedAppointments = groupAppointmentsByDate(appointments);

  const timelineSections = [
    { key: 'today', label: 'Today', data: groupedAppointments.today, isToday: true },
    { key: 'thisWeek', label: 'This Week', data: groupedAppointments.thisWeek },
    { key: 'thisMonth', label: 'This Month', data: groupedAppointments.thisMonth },
    { key: 'lastThreeMonths', label: 'Last 3 Months', data: groupedAppointments.lastThreeMonths },
    { key: 'lastSixMonths', label: 'Last 6 Months', data: groupedAppointments.lastSixMonths },
    { key: 'older', label: 'Older', data: groupedAppointments.older },
  ];

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-50" style={{ height: '100dvh' }}>

      {/* ── Top Bar ── */}
      <div className="flex-shrink-0 bg-white border-b border-slate-100 px-4 pt-safe pb-3">
        <div className="flex items-center justify-between">
          <ClariMeLogo size="small" variant="light" />
          <div className="relative" ref={settingsRef}>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 active:bg-slate-200 transition-colors"
            >
              <SettingsIcon />
            </button>
            {showSettings && (
              <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50 animate-fade-in">
                {(auth?.user?.firstName || auth?.user?.email || auth?.user?.phone) && (
                  <div className="px-4 py-3 border-b border-slate-100">
                    {auth.user.firstName && (
                      <p className="text-sm font-semibold text-slate-800 truncate">{auth.user.firstName}</p>
                    )}
                    {auth.user.email && (
                      <p className="text-xs text-slate-500 truncate">{auth.user.email}</p>
                    )}
                    {auth.user.phone && (
                      <p className="text-xs text-slate-400">{`(${auth.user.phone.slice(0,3)}) ${auth.user.phone.slice(3,6)}-${auth.user.phone.slice(6)}`}</p>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { setShowSettings(false); unregisterPush(); auth?.signOut(); }}
                  className="w-full px-4 py-3 text-left text-sm text-slate-700 active:bg-slate-50 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
                <button
                  onClick={() => { setShowSettings(false); setDeleteAccountStep('confirm'); }}
                  className="w-full px-4 py-3 text-left text-sm text-red-600 active:bg-red-50 transition-colors flex items-center gap-2 border-t border-slate-100"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                  </svg>
                  Delete Account
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Scrollable Content ── */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="px-4 pt-4 pb-4 max-w-2xl mx-auto">

          {/* Lost recording banner — recorder was active but is now gone */}
          {lostRecordingNotice && (
            <div className="mb-4 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900 mb-0.5">Previous recording could not be recovered</p>
                <p className="text-xs text-amber-800">
                  iOS ended the app while it was in the background. For long visits, keep the screen on or tap pause when stepping away.
                </p>
              </div>
              <button
                onClick={() => setLostRecordingNotice(null)}
                className="p-1 -m-1 rounded-full text-amber-700 active:bg-amber-100 flex-shrink-0"
                aria-label="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Greeting */}
          <p className="text-base text-slate-500 mb-4">
            {getGreeting()}{auth?.user?.firstName ? `, ${auth.user.firstName}` : ''}
          </p>

          {/* Record Visit Button — square tile */}
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setView('consent')}
              className="w-36 h-36 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-3xl font-semibold shadow-lg shadow-teal-500/20 active:scale-[0.95] transition-all flex flex-col items-center justify-center gap-2"
            >
              <MicIcon className="w-12 h-12 text-white" />
              <span className="text-base font-semibold">Record</span>
            </button>
          </div>

          {/* ── Tab Toggle ── */}
          <div className="flex mb-4 bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setTab('home')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                tab === 'home'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-slate-400 active:text-slate-600'
              }`}
            >
              <ClipboardIcon className="w-4 h-4" />
              Visits
            </button>
            <button
              onClick={() => setTab('groups')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all relative ${
                tab === 'groups'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-slate-400 active:text-slate-600'
              }`}
            >
              <UsersIcon className="w-4 h-4" />
              Care Groups
              {totalUnread > 0 && tab !== 'groups' && (
                <span className="absolute -top-1 right-4 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </button>
          </div>

          {/* Orphaned recordings banner */}
          {tab === 'home' && orphanedRecordings.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-amber-800">Unsaved recording found</p>
                </div>
                <button
                  onClick={() => {
                    const latest = orphanedRecordings[0];
                    // Pass the original orphan ID so ProcessingAndSummaryScreen
                    // can clear it after a successful upload (not before).
                    setRecordingData({
                      audioBlob: latest.audioBlob,
                      durationSeconds: latest.metadata?.durationSeconds || 0,
                      duration: `${Math.floor((latest.metadata?.durationSeconds || 0) / 60)}m`,
                      retryOrphanId: latest.id,
                    });
                    setView('post-recording');
                  }}
                  className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg active:bg-amber-700"
                >
                  Retry
                </button>
                <button
                  onClick={() => orphanedRecordings.forEach(r => dismissRecording(r.id))}
                  className="text-xs text-amber-600 font-medium"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* ── Tab Content ── */}
          {tab === 'home' ? (
            isLoading ? (
              <div className="flex-1 flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-8 h-8 border-3 border-teal-500/20 border-t-teal-400 rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-400">Loading visits...</p>
                </div>
              </div>
            ) : appointments.length === 0 ? (
              /* ── Empty State ── */
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
                  <ClipboardIcon className="w-8 h-8 text-teal-300" />
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-1">No visits yet</h3>
                <p className="text-sm text-slate-500 text-center max-w-[240px] leading-relaxed">
                  Tap <span className="font-medium text-teal-600">Record Visit</span> at your next appointment to get started.
                </p>
              </div>
            ) : (
              /* ── Visit List ── */
              <div className="space-y-4">
                {timelineSections.map(section => (
                  <TimelineSection
                    key={section.key}
                    label={section.label}
                    appointments={section.data}
                    groups={groups}
                    isToday={section.isToday}
                    onSelectAppointment={(apt) => { setSelectedAppointment(apt); setView('appointment'); }}
                    onSelectGroup={(g) => { setSelectedGroup(g); setView('chat'); }}
                  />
                ))}
              </div>
            )
          ) : (
            /* ── Groups Tab ── */
            (() => {
              const myGroups = groups.filter(g => g.isOwner);
              const memberGroups = groups.filter(g => !g.isOwner);
              const hasAny = groups.length > 0;

              return hasAny ? (
                <div className="space-y-6">
                  {/* Your Care Teams (groups you created) */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-semibold text-slate-700">Your Care Teams</h2>
                      <button
                        onClick={() => setView('create-group')}
                        className="text-xs font-medium text-teal-600 active:text-teal-700 transition-colors flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        New Group
                      </button>
                    </div>
                    {myGroups.length > 0 ? (
                      <GroupsListView groups={myGroups} onSelectGroup={(g) => { setSelectedGroup(g); setView('chat'); }} getUnreadCount={getUnreadCount} />
                    ) : (
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <p className="text-sm text-slate-500">No care teams created yet.</p>
                        <button
                          onClick={() => setView('create-group')}
                          className="mt-2 text-sm font-medium text-teal-600 active:text-teal-700"
                        >
                          Create your first Care Team
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Care Teams You're In (groups where you're a member, not owner) */}
                  {memberGroups.length > 0 && (
                    <div>
                      <h2 className="text-sm font-semibold text-slate-700 mb-3">Care Teams You're In</h2>
                      <GroupsListView groups={memberGroups} onSelectGroup={(g) => { setSelectedGroup(g); setView('chat'); }} getUnreadCount={getUnreadCount} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
                    <UsersIcon className="w-8 h-8 text-teal-300" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-1">No care teams yet</h3>
                  <p className="text-sm text-slate-500 text-center max-w-[240px] leading-relaxed mb-5">
                    Add family members so they get notified after each visit.
                  </p>
                  <button
                    onClick={() => setView('create-group')}
                    className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-xl shadow-md shadow-teal-500/20 active:scale-[0.98] transition-all flex items-center gap-2"
                  >
                    <UsersIcon className="w-4 h-4" />
                    Create Care Team
                  </button>
                </div>
              );
            })()
          )}
        </div>
      </div>

      {/* ── Delete Account modal ── */}
      {deleteAccountStep && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm bg-white rounded-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
            {deleteAccountStep === 'confirm' && (
              <>
                <div className="px-6 pt-6 pb-2">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">Delete your account?</h2>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    This permanently removes your visits, recordings, summaries,
                    Care Groups you own, and chat history. This cannot be undone.
                  </p>
                </div>
                <div className="px-6 pb-6 pt-4 flex flex-col gap-2">
                  <button
                    onClick={() => setDeleteAccountStep('finalConfirm')}
                    className="w-full py-3 rounded-xl bg-red-600 text-white text-sm font-semibold active:bg-red-700 transition-colors"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => { setDeleteAccountStep(null); setDeleteAccountError(null); }}
                    className="w-full py-3 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold active:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {(deleteAccountStep === 'finalConfirm' || deleteAccountStep === 'error') && (
              <>
                <div className="px-6 pt-6 pb-2">
                  <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">
                    Are you sure you want to delete your account?
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    This will erase all your data.
                  </p>
                  {deleteAccountError && (
                    <p className="mt-3 text-sm text-red-600">{deleteAccountError}</p>
                  )}
                </div>
                <div className="px-6 pb-6 pt-4 flex flex-col gap-2">
                  <button
                    onClick={handleConfirmDeleteAccount}
                    className="w-full py-3 rounded-xl bg-red-600 text-white text-sm font-semibold active:bg-red-700 transition-colors"
                  >
                    Yes, delete my account
                  </button>
                  <button
                    onClick={() => { setDeleteAccountStep(null); setDeleteAccountError(null); }}
                    className="w-full py-3 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold active:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {deleteAccountStep === 'deleting' && (
              <div className="px-6 py-10 flex flex-col items-center">
                <div className="w-10 h-10 border-3 border-slate-200 border-t-red-600 rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-slate-700">Deleting your account…</p>
                <p className="text-xs text-slate-500 mt-1">Removing all data from our servers.</p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
